/**
 * Maskenspezifikation (Order IAZI-FORMULARE-HEDO 8.10., S1/S2) — the IAZI Web
 * Tool (HEDO) masks of the three Bewertungstypen as ONE source for the CRM
 * form, the server gate, the prefill, the adapter, the parameter dossier and
 * the show page. Normquelle: `docs/IAZI-WEBTOOL-FELDINVENTAR-2026-10-08.md`
 * (§1.3, §1.5, §2, §3, §4, measured in HEDO on 8.10.) and the order (S2).
 *
 * Per field: `key` (leaf name in the create-DTO / `BewertungWerte`), `quelle`
 * (where the DTO holds it), `label` (HEDO word, WITHOUT the star), `sektion`,
 * `spalte` (HEDO column L/R), `steuerelement`, `optionen` (value → word → API
 * code, in HEDO order), `pflicht` (HEDO: everything without `*`), `api` (input
 * name of the model, `null` = never sent), `modell`, `phase` (2 = after
 * Matteo, not built). Pinned field by field against the inventory and a
 * hand-written JSON transcription in `iazi-maske.test.ts`.
 */
export const IAZI_MASKE_STAND = '2026-10-08';
export const IAZI_MODELL = {
    efh: 'a2',
    ewg: 'a3',
    mwg: 'cv',
};
/** HEDO header line of every mask. */
export const OPTIONALE_FELDER_HINWEIS = 'Optionale Felder sind mit * gekennzeichnet';
/** Inventory §1.3, Lage im Ort 1 … 5 in half steps (order K12: en dash, no spaces). */
export const WORTSTUFEN_1_5 = [
    'Ausserordentlich',
    'Ausserordentlich–Sehr gut',
    'Sehr gut',
    'Sehr gut–Gut',
    'Gut',
    'Gut–Mittel',
    'Mittel',
    'Mittel–Schlecht',
    'Schlecht',
];
/** Inventory §1.3, the seven steps 1 … 4 (Zustand, Bauqualität, Lage im Gebäude). */
export const WORTSTUFEN_1_4 = [
    'Sehr gut',
    'Sehr gut–Gut',
    'Gut',
    'Gut–Mittel',
    'Mittel',
    'Mittel–Schlecht',
    'Schlecht',
];
const stufen = (woerter) => woerter.map((wort, i) => ({ wert: 1 + i / 2, wort, api: 1 + i / 2 }));
const ZUSTAND_WERTE = [
    'sehr_gut',
    'sehr_gut_gut',
    'gut',
    'gut_mittel',
    'mittel',
    'mittel_schlecht',
    'schlecht',
];
const zustandStufen = () => WORTSTUFEN_1_4.map((wort, i) => ({ wert: ZUSTAND_WERTE[i], wort, api: 1 + i / 2 }));
const JA_NEIN = [
    { wert: false, wort: 'Nein', api: 0 },
    { wert: true, wort: 'Ja', api: 1 },
];
/** «Ja» sends the second control's value (year / date), not a 1. */
const JA_NEIN_ZUSATZ = [
    { wert: false, wort: 'Nein', api: 0 },
    { wert: true, wort: 'Ja', api: null },
];
const feld = (key, quelle, label, sektion, spalte, steuerelement, pflicht, api, extra = {}) => ({
    key,
    quelle,
    label,
    sektion,
    spalte,
    steuerelement,
    pflicht,
    api,
    phase: 1,
    ...extra,
});
const phase2 = (key, label, sektion, spalte, steuerelement, api, extra = {}) => ({
    key,
    quelle: 'system',
    label,
    sektion,
    spalte,
    steuerelement,
    pflicht: false,
    api,
    phase: 2,
    ...extra,
});
// ── Koordinaten (all three) ──────────────────────────────────────────────────
const KOORDINATEN = [
    feld('objektId', 'system', 'Objekt-ID', 'Koordinaten', 'L', 'info', true, 'externalKey', {
        nurLesen: true,
    }),
    feld('plz', 'location', 'PLZ / Ort', 'Koordinaten', 'L', 'text', true, 'zip'),
    feld('street', 'address', 'Strasse / Haus-Nr.', 'Koordinaten', 'L', 'text', true, 'street'),
    feld('gemeindeBfs', 'location', 'BFS-Gemeindenummer', 'Koordinaten', 'L', 'zahl', true, null),
    feld('lageImOrt', 'paid', 'Lage im Ort', 'Koordinaten', 'L', 'wortstufe', true, 'qualityMicro', {
        optionen: stufen(WORTSTUFEN_1_5),
    }),
    feld('grundbuch', 'paid', 'Grundbuch', 'Koordinaten', 'R', 'text', false, 'landRegister'),
    feld('grundbuchNr', 'paid', 'Grundbuch-Nr.', 'Koordinaten', 'R', 'text', false, 'landRegisterNumber'),
    phase2('egrid', 'EGRID', 'Koordinaten', 'R', 'text', null),
    phase2('egid', 'EGID', 'Koordinaten', 'R', 'text', null),
    phase2('edid', 'EDID', 'Koordinaten', 'R', 'text', null),
    phase2('ewid', 'EWID', 'Koordinaten', 'R', 'text', null),
    phase2('bilder', 'Bilder', 'Koordinaten', 'voll', 'info', null),
    phase2('assistent.lageImOrt', 'Assistent – Lage im Ort', 'Koordinaten', 'voll', 'info', null),
];
// ── shared rows of the other sections ────────────────────────────────────────
/**
 * Veldcheck 13-10 (A1, inventory «Korrekturen 13.10.2026» 3): HEDO has no star on
 * «Nutzungsart» — Pflicht as in HEDO. Still no API field: never sent, only the
 * form's Pflicht list and the server's 400 gate count it (and the dossier keeps it).
 */
const NUTZUNGSART = (spalte) => feld('nutzungsart', 'paid', 'Nutzungsart', 'Masse', spalte, 'auswahl', true, null, {
    optionen: [
        { wert: 'selbstgenutzt', wort: 'Selbstgenutzt', api: 1 },
        { wert: 'fremdvermietet', wort: 'Fremdvermietet', api: 2 },
    ],
});
const FERIENOBJEKT = feld('ferienobjekt', 'paid', 'Ferienobjekt', 'Masse', 'R', 'jaNein', true, 'primSecFlag', {
    optionen: JA_NEIN,
});
const LUXUSOBJEKT = feld('luxusobjekt', 'paid', 'Luxusobjekt', 'Masse', 'R', 'wortstufe', true, 'luxusFlag', {
    optionen: [
        { wert: 'nein', wort: 'Nein', api: 0 },
        { wert: 'teilweise', wort: 'Teilweise', api: 0.5 },
        { wert: 'ja', wort: 'Ja', api: 1 },
    ],
});
const VOLUMEN = feld('volumen', 'paid', 'Volumen (inkl. Garagen) (m³)', 'Masse', 'L', 'dezimal', true, 'volume');
const VOLUMEN_NORM = (spalte) => feld('volumenNorm', 'paid', 'Norm Raumvolumen', 'Masse', spalte, 'auswahl', true, 'volumeCode', {
    optionen: [
        { wert: 'gvz', wort: 'Gebäudeversicherung', api: 1 },
        { wert: 'sia116', wort: 'SIA 116', api: 2 },
        { wert: 'sia416', wort: 'SIA 416', api: 4 },
        { wert: 'andere', wort: 'Andere', api: 3 },
    ],
});
const GRUNDSTUECK = (spalte) => feld('grundstueck', 'merkmale', 'Grundstücksfläche (m²)', 'Masse', spalte, 'dezimal', true, 'surfaceGround');
const NETTOWOHNFLAECHE = (spalte) => feld('wohnflaeche', 'object', 'Nettowohnfläche (m²)', 'Masse', spalte, 'dezimal', true, 'surfaceLiving');
const BAUJAHR = feld('baujahr', 'object', 'Baujahr', 'Gebäude', 'L', 'zahl', true, 'buildYear');
const SANIERUNG = feld('renovationsjahr', 'paid', 'Sanierung / Jahr', 'Gebäude', 'L', 'jaNein', true, 'renovationYear', {
    optionen: JA_NEIN_ZUSATZ,
    zusatz: {
        key: 'renovationsjahr',
        steuerelement: 'zahl',
        aktivBei: 'ja',
        ariaLabel: 'Sanierungsjahr',
    },
});
const ZUSTAND = feld('zustand', 'object', 'Zustand Gebäude', 'Gebäude', 'L', 'wortstufe', true, 'buildCondition', {
    optionen: zustandStufen(),
});
const BAUQUALITAET = (spalte) => feld('bauqualitaet', 'paid', 'Bauqualität', 'Gebäude', spalte, 'wortstufe', true, 'buildQuality', {
    optionen: stufen(WORTSTUFEN_1_4),
});
const MINERGIE = feld('minergie', 'paid', 'Minergie-Standard', 'Gebäude', 'R', 'auswahl', false, 'minergieCode', {
    optionen: [
        { wert: 'keine', wort: 'Nein', api: 0 },
        { wert: 'minergie', wort: 'Minergie', api: 1 },
        { wert: 'minergie_p', wort: 'Minergie-P', api: 2 },
        { wert: 'minergie_eco', wort: 'Minergie-ECO', api: 3 },
        { wert: 'minergie_p_eco', wort: 'Minergie-P-ECO', api: 4 },
        { wert: 'minergie_a', wort: 'Minergie-A', api: 5 },
        { wert: 'minergie_a_eco', wort: 'Minergie-A-ECO', api: 6 },
    ],
});
const SERVITUTE = feld('servitute', 'paid', 'Wertrelevante Servitute', 'Gebäude', 'R', 'jaNein', true, 'servituteFlag', {
    optionen: JA_NEIN,
});
const BAURECHT = feld('baurecht', 'paid', 'Baurecht / Verfall', 'Gebäude', 'R', 'jaNein', true, 'buildRightDate', {
    optionen: JA_NEIN_ZUSATZ,
    zusatz: {
        key: 'baurechtVerfall',
        steuerelement: 'datum',
        aktivBei: 'ja',
        ariaLabel: 'Baurecht: Verfalldatum',
    },
});
const GEBAEUDE_PHASE2 = [
    phase2('energie', 'Energie (Expander)', 'Gebäude', 'voll', 'info', null),
    phase2('assistent.zustand', 'Assistent – Zustand Gebäude', 'Gebäude', 'voll', 'info', null),
];
const ASSISTENT_BAUQUALITAET = phase2('assistent.bauqualitaet', 'Assistent – Bauqualität', 'Gebäude', 'voll', 'info', null);
const ANZAHL_ZIMMER = feld('raeume', 'object', 'Anzahl Zimmer', 'Elemente', 'L', 'dezimal', true, 'roomNb');
const ZAEHLWEISE = feld('zaehlweiseZimmer', 'paid', 'Zählweise Zimmer', 'Elemente', 'L', 'auswahl', true, 'roomShiftCode', {
    optionen: [
        { wert: 'kueche_nicht', wort: 'Küche nicht gezählt', api: 0 },
        // Veldcheck 13-10 (A4): HEDO's words end in «gezählt» (live dump 13.10.); codes unchanged.
        { wert: 'kueche_halb', wort: 'Küche als ½ Zimmer gezählt', api: 1 },
        { wert: 'kueche_ganz', wort: 'Küche als 1 Zimmer gezählt', api: 2 },
    ],
});
const BADEZIMMER = (spalte) => feld('badezimmer', 'paid', 'Anzahl Badezimmer', 'Elemente', spalte, 'zahl', true, 'bathNb');
const SEPARATE_WC = feld('separateWc', 'paid', 'Anzahl separate WC', 'Elemente', 'R', 'zahl', true, 'sepWcNb', {
    nullErlaubt: true,
});
const ANZAHL_STOCKWERKE = (spalte) => feld('geschosse', 'paid', 'Anzahl Stockwerke', 'Elemente', spalte, 'zahl', true, 'floorNb');
const PARKPLAETZE = [
    feld('garagenparkplaetze', 'paid', 'Anzahl separate Garagen', 'Parkplätze', 'L', 'zahl', true, 'parkCoverNb', {
        nullErlaubt: true,
    }),
    feld('tiefgaragenplaetze', 'paid', 'Anzahl Tiefgaragenplätze', 'Parkplätze', 'L', 'zahl', true, 'parkOpenNb', {
        nullErlaubt: true,
    }),
    feld('aussenparkplaetze', 'paid', 'Anzahl Abstellplätze', 'Parkplätze', 'R', 'zahl', true, 'parkExternalNb', {
        nullErlaubt: true,
    }),
];
const RECHTSGESCHAEFT = [
    phase2('rechtsgeschaeftArt', 'Art des Rechtsgeschäfts', 'Rechtsgeschäft', 'voll', 'auswahl', 'transactionTypeCode', {
        optionen: [
            [1, 'Freihand'],
            [5, 'Kreditablösung'],
            [2, 'Versteigerung'],
            [4, 'Baufinanzierung'],
            [6, 'Erbschaft'],
            [7, 'Schenkung'],
            [3, 'Andere'],
        ].map(([code, wort]) => ({
            wert: code,
            wort: wort,
            api: code,
        })),
    }),
    phase2('transaktionsdatum', 'Transaktionsdatum', 'Rechtsgeschäft', 'voll', 'text', 'transactionDate'),
    phase2('statusGeschaeft', 'Status Geschäft', 'Rechtsgeschäft', 'voll', 'auswahl', 'processTypeCode', {
        optionen: [
            [1, 'Offerte (offen)'],
            [2, 'Offerte (abgebrochen)'],
            [3, 'Transaktion'],
            [4, 'Risikoanalyse'],
        ].map(([code, wort]) => ({
            wert: code,
            wort: wort,
            api: code,
        })),
    }),
    phase2('transaktionspreis', 'Transaktionspreis (CHF)', 'Rechtsgeschäft', 'voll', 'zahl', 'transactionPrice'),
];
const ALLGEMEIN = [
    feld('bemerkungen', 'paid', 'Bemerkungen', 'Allgemein', 'voll', 'text', false, 'info'),
];
const bewertung = (anpassung) => [
    anpassung,
    phase2('begruendung', 'Begründung', 'Bewertung', 'voll', 'text', 'appraiseAddInfo'),
];
const WERTANPASSUNG = phase2('wertanpassung', 'Wertanpassung (CHF)', 'Bewertung', 'voll', 'zahl', 'appraiseAddValue');
// ── EFH (a2) — inventory §2 ──────────────────────────────────────────────────
const EFH = [
    ...KOORDINATEN,
    feld('haustyp', 'merkmale', 'Haustyp', 'Masse', 'L', 'auswahl', true, 'typeHouseCode', {
        optionen: [
            { wert: 'freistehend', wort: 'Freistehend', api: 1 },
            { wert: 'doppel', wort: 'Doppelhaushälfte', api: 2 },
            { wert: 'reihe_mitte', wort: 'Reihenmittelhaus', api: 3 },
            { wert: 'reihe_eck', wort: 'Reiheneckhaus', api: 4 },
            { wert: 'terrasse', wort: 'Terrassenhaus', api: 5 },
        ],
    }),
    NETTOWOHNFLAECHE('L'),
    VOLUMEN,
    VOLUMEN_NORM('L'),
    NUTZUNGSART('L'),
    GRUNDSTUECK('R'),
    feld('dachstockAusbaubar', 'paid', 'Dachstock ausbaubar', 'Masse', 'R', 'jaNein', true, 'roofFlag', {
        optionen: JA_NEIN,
    }),
    FERIENOBJEKT,
    LUXUSOBJEKT,
    phase2('assistent.luxusobjekt', 'Assistent – Luxusobjekt', 'Masse', 'voll', 'info', null),
    BAUJAHR,
    SANIERUNG,
    ZUSTAND,
    BAUQUALITAET('R'),
    MINERGIE,
    SERVITUTE,
    BAURECHT,
    ...GEBAEUDE_PHASE2,
    ASSISTENT_BAUQUALITAET,
    ANZAHL_ZIMMER,
    ZAEHLWEISE,
    BADEZIMMER('R'),
    SEPARATE_WC,
    ...PARKPLAETZE,
    ...RECHTSGESCHAEFT,
    ...ALLGEMEIN,
    ...bewertung(WERTANPASSUNG),
];
// ── EWG (a3) — inventory §3 ──────────────────────────────────────────────────
const EWG = [
    ...KOORDINATEN,
    feld('gebaeudetyp', 'merkmale', 'Gebäudetyp', 'Masse', 'L', 'auswahl', true, 'typeFlatBuildingCode', {
        optionen: [
            { wert: 'mfh', wort: 'Mehrfamilienhaus', api: 1 },
            { wert: 'terrassenhaus', wort: 'Terrassenhaus', api: 2 },
        ],
    }),
    feld('wohnungen', 'paid', 'Anzahl Wohnungen', 'Masse', 'L', 'zahl', true, 'flatNb'),
    feld('wohnungstyp', 'merkmale', 'Wohnungstyp', 'Masse', 'L', 'auswahl', true, 'typeFlatCode', {
        optionen: [
            { wert: 'etage', wort: 'Etagenwohnung', api: 1 },
            { wert: 'penthouse', wort: 'Attikawohnung', api: 2 },
            { wert: 'attika', wort: 'Dachwohnung', api: 3 },
            { wert: 'eg_garten', wort: 'Gartenwohnung', api: 4 },
        ],
    }),
    feld('maisonette', 'merkmale', 'Mehrgeschossig', 'Masse', 'L', 'jaNein', true, 'maisonetteFlag', {
        optionen: JA_NEIN,
    }),
    feld('etage', 'paid', 'Stockwerk', 'Masse', 'L', 'zahl', true, 'floor', { nullErlaubt: true }),
    feld('lageImGebaeude', 'paid', 'Lage im Gebäude', 'Masse', 'L', 'wortstufe', true, 'qualityFlatHouse', {
        optionen: stufen(WORTSTUFEN_1_4),
    }),
    NETTOWOHNFLAECHE('R'),
    feld('balkonflaeche', 'paid', 'Fläche Balkon / Terrasse (m²)', 'Masse', 'R', 'dezimal', true, 'surfaceBalcony', {
        nullErlaubt: true,
    }),
    feld('wertquote', 'paid', 'Wertquote (‰)', 'Masse', 'R', 'zahl', true, 'ownershipPercent'),
    FERIENOBJEKT,
    NUTZUNGSART('R'),
    LUXUSOBJEKT,
    phase2('assistent.lageImGebaeude', 'Assistent – Lage im Gebäude', 'Masse', 'voll', 'info', null),
    phase2('assistent.luxusobjekt', 'Assistent – Luxusobjekt', 'Masse', 'voll', 'info', null),
    BAUJAHR,
    SANIERUNG,
    ZUSTAND,
    feld('wohnungszustand', 'paid', 'Zustand Wohnung', 'Gebäude', 'L', 'wortstufe', true, 'flatCondition', {
        optionen: zustandStufen(),
    }),
    BAUQUALITAET('R'),
    MINERGIE,
    SERVITUTE,
    BAURECHT,
    ...GEBAEUDE_PHASE2,
    phase2('assistent.wohnungszustand', 'Assistent – Zustand Wohnung', 'Gebäude', 'voll', 'info', null),
    ASSISTENT_BAUQUALITAET,
    ANZAHL_ZIMMER,
    ZAEHLWEISE,
    ANZAHL_STOCKWERKE('L'),
    BADEZIMMER('R'),
    SEPARATE_WC,
    ...PARKPLAETZE,
    ...RECHTSGESCHAEFT,
    ...ALLGEMEIN,
    ...bewertung(WERTANPASSUNG),
];
// ── MFH / Renditeliegenschaft (cv) — inventory §4 ────────────────────────────
const hnf = (key, label, spalte, api) => feld(key, 'paid', label, 'Masse', spalte, 'dezimal', true, api, {
    expander: 'hnf',
    nullErlaubt: true,
});
const SPIEGEL = [
    ['zi10', '1-Z Wohnungen', 'L', 'flat10Nb'],
    ['zi15', '1½-Z Wohnungen', 'L', 'flat15Nb'],
    ['zi20', '2-Z Wohnungen', 'L', 'flat20Nb'],
    ['zi25', '2½-Z Wohnungen', 'L', 'flat25Nb'],
    ['zi30', '3-Z Wohnungen', 'L', 'flat30Nb'],
    ['zi35', '3½-Z Wohnungen', 'L', 'flat35Nb'],
    ['zi40', '4-Z Wohnungen', 'L', 'flat40Nb'],
    ['zi45', '4½-Z Wohnungen', 'R', 'flat45Nb'],
    ['zi50', '5-Z Wohnungen', 'R', 'flat50Nb'],
    ['zi55', '5½-Z Wohnungen', 'R', 'flat55Nb'],
    ['zi60', '6-Z Wohnungen', 'R', 'flat60Nb'],
    ['zi65', '6½-Z Wohnungen', 'R', 'flat65Nb'],
    ['zi70', '> 7-Z Wohnungen', 'R', 'flat70Nb'],
];
const MFH = [
    ...KOORDINATEN,
    VOLUMEN,
    GRUNDSTUECK('L'),
    NUTZUNGSART('L'),
    VOLUMEN_NORM('R'),
    feld('nettonutzflaeche', 'system', 'Nettonutzfläche (m²)', 'Masse', 'R', 'info', false, null, {
        nurLesen: true,
        abgeleitet: true,
    }),
    feld('wohnflaeche', 'object', 'Wohnfläche (m²)', 'Masse', 'L', 'dezimal', true, 'surfaceLiving', {
        expander: 'hnf',
    }),
    hnf('bueroflaeche', 'Bürofläche (m²)', 'L', 'surfaceOffice'),
    hnf('gewerbeflaeche', 'Gewerbefläche (m²)', 'L', 'surfaceCommercial'),
    hnf('verkaufsflaeche', 'Verkaufs- / Lagerfläche (m²)', 'R', 'surfaceRetail'),
    hnf('unterrichtsflaeche', 'Unterrichtsfläche (m²)', 'R', 'surfaceEducation'),
    hnf('medizinflaeche', 'Medizinische Fläche (m²)', 'R', 'surfaceMedical'),
    BAUJAHR,
    SANIERUNG,
    ZUSTAND,
    BAUQUALITAET('L'),
    MINERGIE,
    SERVITUTE,
    BAURECHT,
    ...GEBAEUDE_PHASE2,
    ASSISTENT_BAUQUALITAET,
    feld('wohnungen', 'paid', 'Anzahl Wohnungen', 'Elemente', 'L', 'info', true, 'flatNb', {
        nurLesen: true,
        abgeleitet: true,
    }),
    ZAEHLWEISE,
    BADEZIMMER('L'),
    ANZAHL_STOCKWERKE('R'),
    feld('aufzuege', 'paid', 'Anzahl Aufzüge', 'Elemente', 'R', 'zahl', true, 'liftNb', {
        nullErlaubt: true,
    }),
    ...SPIEGEL.map(([g, label, spalte, api]) => feld(`wohnungsspiegel.${g}`, 'paid', label, 'Elemente', spalte, 'zahl', true, api, {
        expander: 'wohnungsspiegel',
        nullErlaubt: true,
    })),
    feld('wohnungsspiegel.loft', 'paid', 'Loft-Wohnungen', 'Elemente', 'R', 'zahl', false, 'flatLoftNb', {
        expander: 'wohnungsspiegel',
        nullErlaubt: true,
    }),
    ...PARKPLAETZE,
    feld('mietanteilGeschaeft', 'paid', 'Mietanteil Geschäft (%)', 'Miete', 'L', 'dezimal', true, 'rentRepartitionPercent', {
        nullErlaubt: true,
    }),
    feld('kategorie', 'system', 'Kategorie', 'Miete', 'L', 'info', false, 'subCat', {
        optionen: [
            { wert: 'wohnen', wort: 'Wohnen', api: 1 },
            { wert: 'gemischt', wort: 'Gemischt', api: 2 },
            { wert: 'buero_gewerbe', wort: 'Büro/Gewerbe', api: 3 },
        ],
        nurLesen: true,
        abgeleitet: true,
    }),
    feld('jahresmiete', 'paid', 'Mietwert nachhaltig (CHF) / p.a.', 'Miete', 'L', 'zahl', true, 'rentAnnual'),
    // Veldcheck 13-10 (A2): Pflicht as in HEDO — information only, never sent (no API field).
    feld('mietertragEffektiv', 'paid', 'Mietertrag effektiv (CHF) / p.a.', 'Miete', 'R', 'zahl', true, null),
    ...RECHTSGESCHAEFT,
    ...ALLGEMEIN,
    ...bewertung(phase2('wertanpassung', 'Anpassung Kapitalisierungssatz', 'Bewertung', 'voll', 'dezimal', 'appraiseAddValue')),
];
const mitModell = (vorlagen, modell) => vorlagen.map((v) => ({ ...v, modell }));
/** The three masks, every field (phase 1 and the phase-2 markers), in HEDO order. */
export const IAZI_MASKE = {
    efh: mitModell(EFH, 'a2'),
    ewg: mitModell(EWG, 'a3'),
    mwg: mitModell(MFH, 'cv'),
};
/** HEDO section order (inventory §2–§4); «Miete» only for the Renditeliegenschaft. */
export function maskenSektionen(typ, phase = 1) {
    const alle = [
        'Koordinaten',
        'Masse',
        'Gebäude',
        'Elemente',
        'Parkplätze',
        ...(typ === 'mwg' ? ['Miete'] : []),
        'Rechtsgeschäft',
        'Allgemein',
        'Bewertung',
    ];
    return phase === 2 ? alle : alle.filter((s) => s !== 'Rechtsgeschäft' && s !== 'Bewertung');
}
/** The fields of one mask; phase 1 (default) = what is built, phase 2 = everything. */
export function maskenFelder(typ, phase = 1) {
    const felder = IAZI_MASKE[typ];
    return phase === 2 ? felder : felder.filter((f) => f.phase === 1);
}
/** The phase-1 field of a mask that sends `api` — `undefined` when none does. */
export function maskenFeldFuerApi(typ, api) {
    return maskenFelder(typ).find((f) => f.api === api);
}
/**
 * The label as the mask shows it: HEDO's star after every optional INPUT — a
 * derived read-only row (Nettonutzfläche, Kategorie: inventory §4 «–» /
 * «(abgeleitet)») is no input and carries none (review 8-10 F3).
 */
export const maskenLabel = (f) => (f.pflicht || f.nurLesen || f.abgeleitet ? f.label : `${f.label} *`);
/** `true` for the values the create-DTO knows as «Objekttyp». */
export const istMaskenTyp = (v) => v === 'efh' || v === 'ewg' || v === 'mwg';
/** The word of a stored value — «Gut» for 2 — or `undefined` when the field has no such option. */
export function optionWort(f, wert) {
    return f.optionen?.find((o) => o.wert === wert)?.wort;
}
/**
 * Master 8-10 (integration with package C): the HEDO label of an API input name
 * in the mask of `typ` — the label source of the Bewertungsbericht annex.
 * «town» is the second half of the row «PLZ / Ort»; a name the mask has no row
 * for (the address resolution `ortId` / `locationId`) is `undefined`.
 */
export function maskeLabel(typ, api) {
    if (api === 'town')
        return maskenFelder(typ).find((f) => f.api === 'zip')?.label;
    return maskenFeldFuerApi(typ, api)?.label;
}
/**
 * Master 8-10: the API input names of the mask of `typ` in mask order (section
 * → L → R → expander) — «town» right after «zip» (one row «PLZ / Ort»). Names
 * without a mask row (`ortId`, `locationId`) are not listed; a caller sorts
 * them after these.
 */
export function maskeVolgorde(typ) {
    return maskenFelder(typ).flatMap((f) => f.api === null ? [] : f.api === 'zip' ? ['zip', 'town'] : [f.api]);
}
/**
 * Integration C ∘ AB (8-10): entries in MASK order — the API names of
 * {@link maskeVolgorde} first, in that order; every other name after them, in
 * the order given (the address resolution `ortId` / `locationId`, which has no
 * row of its own). One rule for the three places that list what went to IAZI —
 * the Bewertungsbericht annex, the CRM table and the Markdown export — because
 * the send order differs from the mask (the adapter sends the address and the
 * V6 ids first, the Objekt-ID after them) and S1/C4 make the mask the order.
 */
export function inMaskeVolgorde(typ, eintraege, name) {
    const rang = new Map(maskeVolgorde(typ).map((n, i) => [n, i]));
    return eintraege
        .map((e, i) => ({ e, r: rang.get(name(e)) ?? rang.size + i }))
        .sort((a, b) => a.r - b.r)
        .map((x) => x.e);
}
